import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { clsx } from 'clsx';
import {
  ArrowPathIcon,
  ChatBubbleLeftRightIcon,
  PaperAirplaneIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import {
  chatAPI,
  ChatConversation,
  ChatMessage,
  ChatUserSummary,
} from '../../services/api';
import {
  disconnectChatSocket,
  emitChatEvent,
  ensureChatSocketConnection,
  getChatSocket,
} from '../../services/chatSocket';
import {
  Avatar,
  Button,
  Card,
  CardHeader,
  PageHero,
  SearchInput,
  Spinner,
  useToast,
} from '../ui';

const POLL_INTERVAL_MS = 60000;
const TYPING_IDLE_MS = 1500;

const getDisplayName = (user?: Partial<ChatUserSummary> | null) => {
  const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim();
  return fullName || user?.email || 'Unknown user';
};

const getConversationTimestamp = (value?: string | null) => {
  if (!value) return 'No messages yet';

  const date = new Date(value);
  const today = new Date();
  const isToday = date.toDateString() === today.toDateString();

  return isToday
    ? date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
    : date.toLocaleDateString([], { month: 'short', day: 'numeric' });
};

const getSortTime = (conversation: ChatConversation) => (
  new Date(conversation.lastMessageAt || conversation.updatedAt || conversation.createdAt).getTime()
);

const sortConversations = (items: ChatConversation[]) => (
  [...items].sort((a, b) => getSortTime(b) - getSortTime(a))
);

const ChatPage: React.FC = () => {
  const { user, token } = useAuth();
  const toast = useToast();
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const joinedConversationIdRef = useRef<number | null>(null);
  const typingTimeoutRef = useRef<number | null>(null);
  const typingConversationIdRef = useRef<number | null>(null);

  const [conversations, setConversations] = useState<ChatConversation[]>([]);
  const [availableUsers, setAvailableUsers] = useState<ChatUserSummary[]>([]);
  const [selectedConversationId, setSelectedConversationId] = useState<number | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [messageDraft, setMessageDraft] = useState('');
  const [conversationsLoading, setConversationsLoading] = useState(true);
  const [usersLoading, setUsersLoading] = useState(true);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [startingConversationId, setStartingConversationId] = useState<number | null>(null);
  const [error, setError] = useState('');
  const [threadError, setThreadError] = useState('');
  const [usersError, setUsersError] = useState('');
  const [typingUserIds, setTypingUserIds] = useState<number[]>([]);
  const [onlineUserIds, setOnlineUserIds] = useState<number[]>([]);
  const [socketConnected, setSocketConnected] = useState(false);

  const selectedConversation = useMemo(
    () => conversations.find((conversation) => conversation.id === selectedConversationId) || null,
    [conversations, selectedConversationId],
  );

  const existingConversationByUserId = useMemo(() => {
    const mapping = new Map<number, ChatConversation>();
    conversations.forEach((conversation) => {
      if (conversation.otherParticipant) {
        mapping.set(conversation.otherParticipant.id, conversation);
      }
    });
    return mapping;
  }, [conversations]);

  const updateConversation = useCallback((updatedConversation: ChatConversation) => {
    setConversations((current) => sortConversations([
      updatedConversation,
      ...current.filter((conversation) => conversation.id !== updatedConversation.id),
    ]));
  }, []);

  const upsertMessage = useCallback((incomingMessage: ChatMessage) => {
    setMessages((current) => {
      const existing = current.find((message) => message.id === incomingMessage.id);
      if (existing) {
        return current.map((message) => (message.id === incomingMessage.id ? incomingMessage : message));
      }

      return [...current, incomingMessage];
    });
  }, []);

  const loadConversations = useCallback(async (silent = false) => {
    try {
      if (!silent) {
        setConversationsLoading(true);
      }

      const data = await chatAPI.getConversations();
      setConversations(sortConversations(data));
      setError('');
    } catch (err: any) {
      if (!silent) {
        setError(err.response?.data?.message || 'Failed to load conversations');
      }
    } finally {
      if (!silent) {
        setConversationsLoading(false);
      }
    }
  }, []);

  const loadAvailableUsers = useCallback(async (search?: string) => {
    try {
      setUsersLoading(true);
      const data = await chatAPI.getAvailableUsers({ search: search || undefined, limit: 20 });
      setAvailableUsers(data);
      setUsersError('');
    } catch (err: any) {
      setUsersError(err.response?.data?.message || 'Failed to load available users');
    } finally {
      setUsersLoading(false);
    }
  }, []);

  const markConversationRead = useCallback(async (conversationId: number, messageId?: number) => {
    try {
      const response = await chatAPI.markConversationRead(conversationId, messageId);
      setConversations((current) => current.map((conversation) => (
        conversation.id === conversationId
          ? {
              ...conversation,
              unreadCount: response.unreadCount,
              currentUserState: {
                lastReadMessageId: response.lastReadMessageId,
                lastReadAt: response.lastReadAt,
                isMuted: conversation.currentUserState?.isMuted ?? false,
                isArchived: conversation.currentUserState?.isArchived ?? false,
              },
            }
          : conversation
      )));
    } catch {
      // Silent background sync failure.
    }
  }, []);

  const loadMessages = useCallback(async (conversationId: number, silent = false) => {
    try {
      if (!silent) {
        setMessagesLoading(true);
      }

      const response = await chatAPI.getConversationMessages(conversationId, { limit: 50 });
      setMessages(response.data);
      setThreadError('');

      const latestMessage = response.data[response.data.length - 1];
      if (latestMessage) {
        await markConversationRead(conversationId, latestMessage.id);
      }
    } catch (err: any) {
      if (!silent) {
        setThreadError(err.response?.data?.message || 'Failed to load messages');
      }
    } finally {
      if (!silent) {
        setMessagesLoading(false);
      }
    }
  }, [markConversationRead]);

  useEffect(() => {
    loadConversations();
    loadAvailableUsers();
  }, [loadAvailableUsers, loadConversations]);

  const stopTyping = useCallback((conversationId?: number | null) => {
    const activeConversationId = conversationId ?? typingConversationIdRef.current;

    if (typingTimeoutRef.current) {
      window.clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = null;
    }

    if (activeConversationId) {
      emitChatEvent('chat:typing:stop', { conversationId: activeConversationId });
    }

    typingConversationIdRef.current = null;
  }, []);

  useEffect(() => {
    if (!token) {
      disconnectChatSocket();
      setSocketConnected(false);
      setOnlineUserIds([]);
      return;
    }

    const socket = ensureChatSocketConnection(token);
    if (!socket) {
      return;
    }

    const handleConnect = () => setSocketConnected(true);
    const handleDisconnect = () => setSocketConnected(false);

    setSocketConnected(socket.connected);
    socket.on('connect', handleConnect);
    socket.on('disconnect', handleDisconnect);

    return () => {
      socket.off('connect', handleConnect);
      socket.off('disconnect', handleDisconnect);
    };
  }, [token]);

  useEffect(() => {
    if (!selectedConversationId && conversations.length > 0) {
      setSelectedConversationId(conversations[0].id);
      return;
    }

    if (selectedConversationId && !conversations.some((conversation) => conversation.id === selectedConversationId)) {
      setSelectedConversationId(conversations[0]?.id ?? null);
    }
  }, [conversations, selectedConversationId]);

  useEffect(() => {
    if (!selectedConversationId) {
      setMessages([]);
      setTypingUserIds([]);
      return;
    }

    loadMessages(selectedConversationId);
  }, [loadMessages, selectedConversationId]);

  useEffect(() => {
    const socket = getChatSocket();
    if (!socket || !user) {
      return;
    }

    const handleConnected = () => setSocketConnected(true);
    const handlePresenceSnapshot = (payload: { userIds: number[] }) => {
      setOnlineUserIds(payload.userIds);
    };
    const handleUserOnline = (payload: { userId: number }) => {
      setOnlineUserIds((current) => (current.includes(payload.userId) ? current : [...current, payload.userId]));
    };
    const handleUserOffline = (payload: { userId: number }) => {
      setOnlineUserIds((current) => current.filter((userId) => userId !== payload.userId));
    };
    const handleConversationUpdated = () => {
      void loadConversations(true);
    };
    const handleMessageRead = () => {
      void loadConversations(true);
    };
    const handleMessageNew = (payload: { conversationId: number; message: ChatMessage }) => {
      void loadConversations(true);

      if (payload.conversationId !== selectedConversationId) {
        return;
      }

      upsertMessage(payload.message);
      setTypingUserIds((current) => current.filter((userId) => userId !== payload.message.senderId));

      if (payload.message.senderId && payload.message.senderId !== user.id) {
        void markConversationRead(payload.conversationId, payload.message.id);
      }
    };
    const handleTyping = (payload: { conversationId: number; userId: number; isTyping: boolean }) => {
      if (payload.conversationId !== selectedConversationId || payload.userId === user.id) {
        return;
      }

      setTypingUserIds((current) => (
        payload.isTyping
          ? (current.includes(payload.userId) ? current : [...current, payload.userId])
          : current.filter((userId) => userId !== payload.userId)
      ));
    };

    socket.on('chat:connected', handleConnected);
    socket.on('chat:presence:snapshot', handlePresenceSnapshot);
    socket.on('chat:user:online', handleUserOnline);
    socket.on('chat:user:offline', handleUserOffline);
    socket.on('chat:conversation:updated', handleConversationUpdated);
    socket.on('chat:message:read', handleMessageRead);
    socket.on('chat:message:new', handleMessageNew);
    socket.on('chat:typing', handleTyping);

    return () => {
      socket.off('chat:connected', handleConnected);
      socket.off('chat:presence:snapshot', handlePresenceSnapshot);
      socket.off('chat:user:online', handleUserOnline);
      socket.off('chat:user:offline', handleUserOffline);
      socket.off('chat:conversation:updated', handleConversationUpdated);
      socket.off('chat:message:read', handleMessageRead);
      socket.off('chat:message:new', handleMessageNew);
      socket.off('chat:typing', handleTyping);
    };
  }, [loadConversations, markConversationRead, selectedConversationId, upsertMessage, user]);

  useEffect(() => {
    if (!selectedConversationId || !token) {
      stopTyping(joinedConversationIdRef.current);

      if (joinedConversationIdRef.current) {
        emitChatEvent('chat:leave', { conversationId: joinedConversationIdRef.current });
        joinedConversationIdRef.current = null;
      }

      setTypingUserIds([]);
      return;
    }

    const previousConversationId = joinedConversationIdRef.current;

    if (previousConversationId && previousConversationId !== selectedConversationId) {
      stopTyping(previousConversationId);
      emitChatEvent('chat:leave', { conversationId: previousConversationId });
    }

    emitChatEvent('chat:join', { conversationId: selectedConversationId });
    joinedConversationIdRef.current = selectedConversationId;
    setTypingUserIds([]);

    return () => {
      if (joinedConversationIdRef.current === selectedConversationId) {
        stopTyping(selectedConversationId);
        emitChatEvent('chat:leave', { conversationId: selectedConversationId });
        joinedConversationIdRef.current = null;
      }
    };
  }, [selectedConversationId, stopTyping, token]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      loadConversations(true);
      if (selectedConversationId) {
        loadMessages(selectedConversationId, true);
      }
    }, POLL_INTERVAL_MS);

    return () => window.clearInterval(intervalId);
  }, [loadConversations, loadMessages, selectedConversationId]);

  useEffect(() => () => {
    stopTyping();
    if (joinedConversationIdRef.current) {
      emitChatEvent('chat:leave', { conversationId: joinedConversationIdRef.current });
      joinedConversationIdRef.current = null;
    }
  }, [stopTyping]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleStartConversation = async (targetUser: ChatUserSummary) => {
    const existingConversation = existingConversationByUserId.get(targetUser.id);
    if (existingConversation) {
      setSelectedConversationId(existingConversation.id);
      return;
    }

    try {
      setStartingConversationId(targetUser.id);
      const conversation = await chatAPI.createDirectConversation(targetUser.id);
      updateConversation(conversation);
      setSelectedConversationId(conversation.id);
      toast.success('Chat ready', `Conversation opened with ${getDisplayName(targetUser)}`);
    } catch (err: any) {
      toast.error('Unable to start chat', err.response?.data?.message || 'Please try again.');
    } finally {
      setStartingConversationId(null);
    }
  };

  const handleRefresh = async () => {
    await loadConversations();
    if (selectedConversationId) {
      await loadMessages(selectedConversationId);
    }
  };

  const handleSendMessage = async () => {
    if (!selectedConversationId || !messageDraft.trim()) {
      return;
    }

    try {
      setSending(true);
      const message = await chatAPI.sendMessage(selectedConversationId, messageDraft.trim());
      stopTyping(selectedConversationId);
      setMessageDraft('');
      upsertMessage(message);

      const currentConversation = conversations.find((conversation) => conversation.id === selectedConversationId);
      if (currentConversation) {
        updateConversation({
          ...currentConversation,
          lastMessage: message,
          lastMessageAt: message.createdAt,
          unreadCount: 0,
          currentUserState: {
            lastReadMessageId: message.id,
            lastReadAt: message.createdAt,
            isMuted: currentConversation.currentUserState?.isMuted ?? false,
            isArchived: currentConversation.currentUserState?.isArchived ?? false,
          },
        });
      }

      await loadConversations(true);
    } catch (err: any) {
      toast.error('Failed to send message', err.response?.data?.message || 'Please try again.');
    } finally {
      setSending(false);
    }
  };

  const handleMessageKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      void handleSendMessage();
    }
  };

  const handleDraftChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const nextValue = event.target.value;
    setMessageDraft(nextValue);

    if (!selectedConversationId) {
      return;
    }

    if (!nextValue.trim()) {
      stopTyping(selectedConversationId);
      return;
    }

    if (typingConversationIdRef.current !== selectedConversationId) {
      if (typingConversationIdRef.current) {
        stopTyping(typingConversationIdRef.current);
      }

      emitChatEvent('chat:typing:start', { conversationId: selectedConversationId });
      typingConversationIdRef.current = selectedConversationId;
    }

    if (typingTimeoutRef.current) {
      window.clearTimeout(typingTimeoutRef.current);
    }

    typingTimeoutRef.current = window.setTimeout(() => {
      stopTyping(selectedConversationId);
    }, TYPING_IDLE_MS);
  };

  const selectedOtherParticipant = selectedConversation?.otherParticipant;
  const selectedOtherParticipantOnline = !!selectedOtherParticipant && onlineUserIds.includes(selectedOtherParticipant.id);
  const typingIndicator = typingUserIds.length > 0 ? 'Typing…' : null;

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8 space-y-6">
      <PageHero
        title="Team Chat"
        subtitle={socketConnected
          ? 'Realtime chat is connected. Messages, read receipts, and typing update instantly.'
          : 'Connecting to realtime chat...'}
        icon={<ChatBubbleLeftRightIcon className="w-5 h-5 lg:w-6 lg:h-6 text-white drop-shadow-lg" />}
        accentColor="cyan"
      >
        <div className="mt-2">
          <Button
            variant="outline"
            leftIcon={<ArrowPathIcon className="h-5 w-5" />}
            onClick={handleRefresh}
          >
            Refresh chat
          </Button>
        </div>
      </PageHero>

      {error && (
        <Card variant="bordered" className="border-error-200 bg-error-50 text-error-700">
          {error}
        </Card>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-[24rem,minmax(0,1fr)] gap-6">
        <div className="space-y-6">
          <Card variant="glass">
            <CardHeader
              title="Start a conversation"
              subtitle="Search admins and salesmen in your organization."
            />
            <SearchInput
              placeholder="Search by name or email"
              loading={usersLoading}
              onSearch={(value) => loadAvailableUsers(value.trim())}
            />
            {usersError && <p className="mt-3 text-sm text-error-600">{usersError}</p>}
            <div className="mt-4 max-h-72 space-y-3 overflow-y-auto pr-1">
              {usersLoading ? (
                <div className="flex justify-center py-6"><Spinner size="lg" /></div>
              ) : availableUsers.length === 0 ? (
                <p className="text-sm text-neutral-500">No available users found.</p>
              ) : (
                availableUsers.map((candidate) => {
                  const existingConversation = existingConversationByUserId.get(candidate.id);
                  return (
                    <div
                      key={candidate.id}
                      className="flex items-center justify-between gap-3 rounded-xl border border-neutral-200 bg-white/70 px-3 py-3"
                    >
                      <div className="flex min-w-0 items-center gap-3">
                        <div className="relative">
                          <Avatar name={getDisplayName(candidate)} size="sm" />
                          {onlineUserIds.includes(candidate.id) && (
                            <span className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-white bg-emerald-500" />
                          )}
                        </div>
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium text-neutral-900">{getDisplayName(candidate)}</p>
                          <p className="truncate text-xs text-neutral-500">{candidate.email}</p>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant={existingConversation ? 'secondary' : 'primary'}
                        isLoading={startingConversationId === candidate.id}
                        onClick={() => handleStartConversation(candidate)}
                      >
                        {existingConversation ? 'Open' : 'Message'}
                      </Button>
                    </div>
                  );
                })
              )}
            </div>
          </Card>

          <Card variant="default">
            <CardHeader
              title="Conversations"
              subtitle={conversations.length > 0 ? `${conversations.length} active chats` : 'No conversations yet'}
            />
            <div className="max-h-[32rem] space-y-2 overflow-y-auto pr-1">
              {conversationsLoading ? (
                <div className="flex justify-center py-8"><Spinner size="lg" /></div>
              ) : conversations.length === 0 ? (
                <p className="text-sm text-neutral-500">Start a new chat from the panel above.</p>
              ) : (
                conversations.map((conversation) => {
                  const person = conversation.otherParticipant;
                  const isSelected = conversation.id === selectedConversationId;

                  return (
                    <button
                      key={conversation.id}
                      type="button"
                      onClick={() => setSelectedConversationId(conversation.id)}
                      className={clsx(
                        'w-full rounded-xl border px-3 py-3 text-left transition-all',
                        isSelected
                          ? 'border-primary-300 bg-primary-50 shadow-sm'
                          : 'border-neutral-200 bg-white hover:border-neutral-300 hover:bg-neutral-50',
                      )}
                    >
                      <div className="flex items-start gap-3">
                        <div className="relative">
                          <Avatar name={getDisplayName(person)} size="sm" />
                          {person && onlineUserIds.includes(person.id) && (
                            <span className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-white bg-emerald-500" />
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <p className="truncate text-sm font-semibold text-neutral-900">
                                {getDisplayName(person)}
                              </p>
                              <p className="truncate text-xs text-neutral-500">
                                {conversation.lastMessage?.body || 'No messages yet'}
                              </p>
                            </div>
                            <div className="flex flex-col items-end gap-2">
                              <span className="text-xs text-neutral-400">
                                {getConversationTimestamp(conversation.lastMessageAt || conversation.updatedAt)}
                              </span>
                              {conversation.unreadCount > 0 && (
                                <span className="rounded-full bg-primary-600 px-2 py-0.5 text-xs font-semibold text-white">
                                  {conversation.unreadCount}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </Card>
        </div>

        <Card variant="default" padding="none" className="min-h-[42rem] overflow-hidden">
          {!selectedConversation ? (
            <div className="flex h-full min-h-[30rem] items-center justify-center px-6 text-center">
              <div>
                <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-primary-100 text-primary-600">
                  <ChatBubbleLeftRightIcon className="h-7 w-7" />
                </div>
                <h3 className="text-lg font-semibold text-neutral-900">Select a conversation</h3>
                <p className="mt-2 text-sm text-neutral-500">
                  Choose an existing chat or start a new one from the left panel.
                </p>
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between gap-4 border-b border-neutral-200 bg-white px-5 py-4">
                <div className="flex min-w-0 items-center gap-3">
                  <div className="relative">
                    <Avatar name={getDisplayName(selectedConversation.otherParticipant)} size="md" />
                    {selectedOtherParticipantOnline && (
                      <span className="absolute -bottom-0.5 -right-0.5 h-3.5 w-3.5 rounded-full border-2 border-white bg-emerald-500" />
                    )}
                  </div>
                  <div className="min-w-0">
                    <h3 className="truncate text-lg font-semibold text-neutral-900">
                      {getDisplayName(selectedConversation.otherParticipant)}
                    </h3>
                    <p className="truncate text-sm text-neutral-500">
                      {typingIndicator
                        || (selectedOtherParticipantOnline ? 'Online now' : selectedConversation.otherParticipant?.email)
                        || 'Direct conversation'}
                    </p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  leftIcon={<ArrowPathIcon className="h-4 w-4" />}
                  onClick={() => selectedConversationId && loadMessages(selectedConversationId)}
                >
                  Refresh
                </Button>
              </div>

              <div className="h-[32rem] overflow-y-auto bg-neutral-50 px-5 py-5">
                {messagesLoading ? (
                  <div className="flex h-full items-center justify-center"><Spinner size="lg" /></div>
                ) : threadError ? (
                  <div className="rounded-xl border border-error-200 bg-error-50 px-4 py-3 text-sm text-error-700">
                    {threadError}
                  </div>
                ) : messages.length === 0 ? (
                  <div className="flex h-full items-center justify-center text-center">
                    <div>
                      <p className="text-sm font-medium text-neutral-700">No messages yet</p>
                      <p className="mt-1 text-sm text-neutral-500">Send the first message to start the conversation.</p>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {messages.map((message) => {
                      const isOwnMessage = message.senderId === user?.id;
                      return (
                        <div
                          key={message.id}
                          className={clsx('flex', isOwnMessage ? 'justify-end' : 'justify-start')}
                        >
                          <div className={clsx('max-w-[80%] rounded-2xl px-4 py-3 shadow-sm', isOwnMessage ? 'bg-primary-600 text-white' : 'bg-white text-neutral-900 border border-neutral-200')}>
                            {!isOwnMessage && (
                              <p className="mb-1 text-xs font-semibold text-neutral-500">
                                {getDisplayName(message.sender)}
                              </p>
                            )}
                            <p className="whitespace-pre-wrap break-words text-sm">{message.body}</p>
                            <p className={clsx('mt-2 text-xs', isOwnMessage ? 'text-primary-100' : 'text-neutral-400')}>
                              {new Date(message.createdAt).toLocaleString([], {
                                month: 'short',
                                day: 'numeric',
                                hour: 'numeric',
                                minute: '2-digit',
                              })}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                    <div ref={messagesEndRef} />
                  </div>
                )}
              </div>

              <div className="border-t border-neutral-200 bg-white px-5 py-4">
                <label className="mb-2 block text-sm font-medium text-neutral-700">
                  Message {getDisplayName(selectedConversation.otherParticipant)}
                </label>
                <textarea
                  rows={3}
                  value={messageDraft}
                  onChange={handleDraftChange}
                  onBlur={() => stopTyping(selectedConversationId)}
                  onKeyDown={handleMessageKeyDown}
                  placeholder="Type your message..."
                  className="w-full rounded-xl border border-neutral-300 px-4 py-3 text-sm text-neutral-900 shadow-sm transition-all focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
                />
                <div className="mt-3 flex items-center justify-between gap-3">
                  <p className="text-xs text-neutral-500">Press Enter to send, Shift + Enter for a new line.</p>
                  <Button
                    onClick={handleSendMessage}
                    isLoading={sending}
                    disabled={!messageDraft.trim() || sending}
                    rightIcon={<PaperAirplaneIcon className="h-5 w-5" />}
                  >
                    Send message
                  </Button>
                </div>
              </div>
            </>
          )}
        </Card>
      </div>
    </div>
  );
};

export default ChatPage;